import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import Timeline from "@/components/feature/Timeline";
import StoryCard from "@/components/feature/StoryCard";
import { getRelatedStories, getStoryById, stories } from "@/mocks/stories";

const ORIGIN = "https://laisih.cn";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const story = id ? getStoryById(id) : undefined;
  const [copied, setCopied] = useState(false);

  if (!story) {
    return (
      <SiteLayout>
        <Seo
          title="追踪记录不存在｜黑瓜爆料"
          description="你访问的追踪记录不存在或已被调整，请返回全部追踪列表重新查找。"
          keywords="黑瓜爆料,追踪记录,页面不存在"
          path="/news"
        />
        <div className="mx-auto flex max-w-2xl flex-col items-center px-4 py-24 text-center md:py-32">
          <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-search-line text-2xl" />
          </span>
          <h1 className="mb-3 font-heading text-2xl font-bold text-foreground-950">
            这条追踪记录不存在
          </h1>
          <p className="mb-6 text-sm leading-relaxed text-foreground-600">
            可能是链接有误，或该记录已被合并进另一条时间线。回到列表页可以查看全部正在追踪的事件。
          </p>
          <Link
            to="/news"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" />
            返回全部追踪
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const sameCategory = getRelatedStories(story, 3);
  const related =
    sameCategory.length >= 3
      ? sameCategory
      : [...sameCategory, ...stories.filter((s) => s.id !== story.id && !sameCategory.includes(s))].slice(
          0,
          3,
        );

  const latestNode = story.timeline[story.timeline.length - 1];
  const verifiedCount = story.timeline.filter((node) => node.verified).length;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: story.title,
      description: story.summary,
      datePublished: story.date,
      dateModified: `${story.date}T09:00:00+08:00`,
      inLanguage: "zh-CN",
      articleSection: story.category,
      keywords: story.tags.join(","),
      image: story.cover,
      author: { "@type": "Organization", name: story.author },
      publisher: {
        "@type": "Organization",
        name: "黑瓜爆料编辑部",
        url: ORIGIN,
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `${ORIGIN}/news/${story.id}`,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: ORIGIN },
        { "@type": "ListItem", position: 2, name: "全部追踪", item: `${ORIGIN}/news` },
        {
          "@type": "ListItem",
          position: 3,
          name: story.title,
          item: `${ORIGIN}/news/${story.id}`,
        },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title={`${story.title}｜黑瓜爆料 事件追踪`}
        description={`${story.summary} 黑瓜爆料持续跟进该事件的当事人回应、反转节点与最新进展，完整时间线共 ${story.timeline.length} 个节点。`}
        keywords={`${story.tags.join(",")},黑瓜爆料,爆料后续,最新进展`}
        path={`/news/${story.id}`}
        type="article"
        jsonLd={jsonLd}
      />

      <article>
        <header className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-[1360px] px-4 py-8 md:px-6 md:py-12">
            <nav aria-label="面包屑导航" className="mb-5">
              <ol className="flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
                <li className="flex items-center gap-2">
                  <Link to="/" className="transition-colors hover:text-primary-600">
                    首页
                  </Link>
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </li>
                <li className="flex items-center gap-2">
                  <Link to="/news" className="transition-colors hover:text-primary-600">
                    全部追踪
                  </Link>
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </li>
                <li>
                  <span className="line-clamp-1 text-foreground-800">{story.title}</span>
                </li>
              </ol>
            </nav>

            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Link
                to={`/news?category=${encodeURIComponent(story.category)}`}
                className="rounded-md bg-foreground-950 px-2.5 py-1 font-label text-xs text-background-100 transition-colors hover:bg-primary-600"
              >
                {story.category}
              </Link>
              <StatusBadge status={story.status} size="md" />
            </div>

            <h1 className="mb-4 max-w-4xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              {story.title}
            </h1>

            <p className="mb-5 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {story.summary}
            </p>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-background-300 pt-4 font-label text-xs text-foreground-500 md:text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line" />
                {story.author}
              </span>
              <span className="flex items-center gap-1.5 tabular-nums">
                <i className="ri-calendar-line" />
                {story.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-fire-line text-accent-500" />
                热度 {story.heat}
              </span>
              <span className="flex items-center gap-1.5 tabular-nums">
                <i className="ri-eye-line" />
                {story.views.toLocaleString("zh-CN")} 次阅读
              </span>
              <button
                type="button"
                onClick={handleCopy}
                className="flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className={copied ? "ri-check-line text-secondary-600" : "ri-link"} />
                {copied ? "链接已复制" : "复制链接"}
              </button>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-[1360px] px-4 py-8 md:px-6 md:py-12">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-8">
              <figure className="mb-8 overflow-hidden rounded-lg border border-background-300">
                <div className="h-[240px] w-full bg-background-200 md:h-[400px]">
                  <img
                    src={story.cover}
                    alt={`${story.title} - 事件追踪封面图`}
                    title={`${story.title} 黑瓜爆料事件追踪`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <figcaption className="bg-background-100 px-4 py-3 font-label text-xs text-foreground-500">
                  图为该事件相关公开物料整理，仅作示意，与当事人无直接关联。
                </figcaption>
              </figure>

              <section>
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-4">
                  <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-route-line text-base" />
                    </span>
                    事件时间线
                  </h2>
                  <span className="font-label text-xs text-foreground-500 md:text-sm">
                    共 {story.timeline.length} 个节点 · 已核实 {verifiedCount} 个
                  </span>
                </div>
                <Timeline nodes={story.timeline} />
              </section>

              <section className="mt-10">
                <h2 className="mb-5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  编辑部观察
                </h2>
                <div className="space-y-4">
                  {story.content.map((paragraph, index) => (
                    <p
                      key={index}
                      className="text-sm leading-loose text-foreground-700 md:text-base md:leading-loose"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>

              <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-100 p-4 md:p-5">
                <p className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground-600">
                  <i className="ri-information-line mt-0.5 text-primary-600" />
                  本条记录为追踪过程存档，标注「待核实」的节点仍在求证中，不代表最终结论。如后续信息发生变化，我们会在本条时间线内追加更正节点。
                </p>
              </div>
            </div>

            <aside className="flex flex-col gap-6 lg:col-span-4">
              <div className="rounded-lg border border-background-300 bg-background-50 p-5 lg:sticky lg:top-28">
                <h2 className="mb-4 border-b border-background-200 pb-3 font-heading text-base font-bold text-foreground-950">
                  事件速览
                </h2>
                <dl className="mb-5 flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-3">
                    <dt className="font-label text-sm text-foreground-500">当前状态</dt>
                    <dd>
                      <StatusBadge status={story.status} />
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="font-label text-sm text-foreground-500">所属分类</dt>
                    <dd className="font-label text-sm text-foreground-900">{story.category}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="font-label text-sm text-foreground-500">时间线节点</dt>
                    <dd className="font-label text-sm tabular-nums text-foreground-900">
                      {story.timeline.length} 个
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="font-label text-sm text-foreground-500">最近更新</dt>
                    <dd className="font-label text-sm tabular-nums text-foreground-900">
                      {latestNode ? latestNode.time : story.date}
                    </dd>
                  </div>
                </dl>

                <h3 className="mb-3 border-t border-background-200 pt-4 font-label text-xs font-medium uppercase tracking-[0.16em] text-foreground-500">
                  相关标签
                </h3>
                <div className="flex flex-wrap gap-2">
                  {story.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/news?q=${encodeURIComponent(tag)}`}
                      className="rounded-md bg-secondary-50 px-2.5 py-1 font-label text-xs text-secondary-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <section className="border-t border-background-200 bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-[1360px] px-4 md:px-6">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              相关追踪
            </h2>
            <Link
              to="/news"
              className="flex items-center gap-1 whitespace-nowrap font-label text-sm text-foreground-700 transition-colors hover:text-primary-600"
            >
              查看全部
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item, index) => (
              <StoryCard key={item.id} story={item} variant="grid" index={index} />
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}